// What the Findings console keeps: the filter (whose cases, and whether
// closed ones show), the search, the quick filter and the order the reader
// chose, and the cases kept in the list after a decision (`kept`).
//
// The filter, the order and the severity sections folded away are kept for the
// browser tab. The search itself stays in memory: it is tenant content and
// never goes in the URL.
import { useSyncExternalStore } from "react";
import { ASSIGNEES, SORTS } from "./findingsConsole.js";

const TAB_KEY = "origin-findings-console-v1";

function readTab() {
    try {
        const v = JSON.parse(globalThis.sessionStorage?.getItem(TAB_KEY) || "null");
        // A person is kept by user id.
        const person = typeof v?.assignee === "string" && v.assignee.length <= 64 && !ASSIGNEES.includes(v.assignee) ? v.assignee : null;
        return {
            assignee: ASSIGNEES.includes(v?.assignee) ? v.assignee : person || "all",
            closed: v?.closed === true,
            cleared: v?.cleared === true,
            sort: SORTS.includes(v?.sort) ? v.sort : "severity",
            folded: Array.isArray(v?.folded) ? v.folded.filter((x) => typeof x === "string").slice(0, 8) : [],
        };
    } catch {
        return { assignee: "all", closed: false, cleared: false, sort: "severity", folded: [] };
    }
}

let state = { ...readTab(), query: "", quick: "all", kept: [] };
const listeners = new Set();
const emit = () => { for (const fn of listeners) fn(); };
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const read = () => state;

// A case a decision took or assigned stays listed where it was, whoever holds
// it now, until the reader changes whose cases show, or which.
const RELISTS = ["assignee", "closed", "cleared", "query", "quick"];

export function setConsole(patch) {
    const next = { ...state, ...patch };
    if (Object.keys(patch).every((k) => Object.is(state[k], next[k]))) return;
    if (!("kept" in patch) && next.kept.length && RELISTS.some((k) => !Object.is(state[k], next[k]))) next.kept = [];
    state = next;
    if ("assignee" in patch || "closed" in patch || "cleared" in patch || "sort" in patch || "folded" in patch) {
        try { globalThis.sessionStorage?.setItem(TAB_KEY, JSON.stringify({ assignee: state.assignee, closed: state.closed, cleared: state.cleared, sort: state.sort, folded: state.folded })); } catch { /* this tab only */ }
    }
    emit();
}

export function useConsole() {
    return useSyncExternalStore(subscribe, read, read);
}

/** Keeps the cases `keep` listed where they are and lets `release` go (entry ids). */
export function keepCases(keep = [], release = []) {
    const gone = new Set([...keep, ...release]);
    setConsole({ kept: [...state.kept.filter((id) => !gone.has(id)), ...keep] });
}

export function _resetConsoleForTests() {
    state = { ...readTab(), query: "", quick: "all", kept: [] };
    emit();
}
